// Dokumen, the register.
//   list_header     plain spacing  h1, result count, Buat Dokumen
//   filter_toolbar  card           search, status, tahap, Milik saya (core.toolbar_fields)
//   records         rows           a table from 1024px, stacked rows below (DataTable responsive contract)
// Filters live in the query string, so Beranda counts deep link here.
import { useEffect, useId, useState } from "react";
import * as API from "../api";
import { useUser } from "../auth";
import { docTypeLabel, loadCatalog } from "../lib/catalog";
import { formatCount, formatDateTime } from "../lib/format";
import { canGenerate, STATUS_LABEL, STATUS_ORDER, TAHAP_LABEL, TAHAP_ORDER } from "../lib/labels";
import { navigate, setQuery, useLocation } from "../router";
import { EmptyState, Notice, PageHeader, SearchInput, SkeletonRows, StatusBadge, Switch, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

function isStatus(v: string | null): v is API.DocStatus {
  return v !== null && (STATUS_ORDER as string[]).includes(v);
}

function isTahap(v: string | null): v is API.Tahap {
  return v !== null && (TAHAP_ORDER as string[]).includes(v);
}

export function DocumentsScreen() {
  useTitle("Dokumen");
  const user = useUser();
  const loc = useLocation();
  const status = isStatus(loc.query.get("status")) ? (loc.query.get("status") as API.DocStatus) : undefined;
  const tahap = isTahap(loc.query.get("tahap")) ? (loc.query.get("tahap") as API.Tahap) : undefined;
  const mine = loc.query.get("mine") === "1";
  const q = loc.query.get("q") ?? "";

  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);
  useEffect(() => {
    if (search === q) return;
    const t = setTimeout(() => setQuery({ q: search.trim() || undefined }), 300);
    return () => clearTimeout(t);
  }, [search, q]);

  const docs = useAsync(
    () => API.listDocuments({ status, tahap, q: q || undefined, mine: mine ? "1" : undefined }),
    [status, tahap, q, mine],
  );
  const catalog = useAsync(() => loadCatalog(user.id).catch(() => [] as API.CatalogTahap[]), [user.id]);
  const filtered = Boolean(status || tahap || q || mine);
  const activeFilters = [status, tahap, mine ? "1" : undefined].filter(Boolean).length;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const ids = useId();

  return (
    <>
      <PageHeader
        divided
        title="Dokumen"
        lead={docs.data ? `${formatCount(docs.data.length)} dokumen${filtered ? " sesuai filter" : ""}` : "Memuat daftar dokumen"}
        actions={
          canGenerate(user.role) ? (
            <a className="btn" href="/">
              <Glyph name="messageAdd" />
              <span>Minta lewat Chat</span>
            </a>
          ) : null
        }
      />

      <section className="toolbar" aria-label="Filter dokumen">
        <div className="field-row" data-cols="4">
          <div className="field">
            <label htmlFor={`${ids}q`}>Cari judul</label>
            <SearchInput id={`${ids}q`} value={search} onChange={setSearch} placeholder="Judul dokumen" />
          </div>
          <div className="field toolbar-extra" data-open={filtersOpen ? "" : undefined}>
            <label htmlFor={`${ids}status`}>Status</label>
            <select id={`${ids}status`} value={status ?? ""} onChange={(e) => setQuery({ status: e.currentTarget.value || undefined })}>
              <option value="">Semua status</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field toolbar-extra" data-open={filtersOpen ? "" : undefined}>
            <label htmlFor={`${ids}tahap`}>Tahap</label>
            <select id={`${ids}tahap`} value={tahap ?? ""} onChange={(e) => setQuery({ tahap: e.currentTarget.value || undefined })}>
              <option value="">Semua tahap</option>
              {TAHAP_ORDER.map((t) => (
                <option key={t} value={t}>
                  {TAHAP_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="field toolbar-extra" data-open={filtersOpen ? "" : undefined}>
            <span className="field-label" id={`${ids}mine-label`}>
              Pembuat
            </span>
            <Switch id={`${ids}mine`} label="Milik saya" checked={mine} onChange={(v) => setQuery({ mine: v ? "1" : undefined })} />
          </div>
        </div>
        <div className="toolbar-foot">
          <button
            type="button"
            className="btn btn-secondary toolbar-toggle"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            <Glyph name="filter" />
            <span>{activeFilters ? `Filter (${activeFilters})` : "Filter"}</span>
          </button>
          {filtered ? (
            <button type="button" className="btn btn-ghost" onClick={() => setQuery({ status: undefined, tahap: undefined, q: undefined, mine: undefined })}>
              Hapus filter
            </button>
          ) : null}
        </div>
      </section>

      <section className="region" aria-label="Daftar dokumen" aria-busy={docs.loading || undefined}>
        {docs.loading && !docs.data ? (
          <SkeletonRows count={6} label="Memuat dokumen" />
        ) : docs.error ? (
          <Notice tone="danger" title="Daftar dokumen belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={docs.reload}>Muat ulang</button>}>
            <p>{API.errorMessage(docs.error)}</p>
          </Notice>
        ) : docs.data && docs.data.length === 0 ? (
          filtered ? (
            <EmptyState
              icon="filter"
              title="Tidak ada dokumen yang cocok"
              body="Ubah kata kunci atau hapus filter untuk melihat semua dokumen."
              action={
                <button type="button" className="btn btn-secondary" onClick={() => setQuery({ status: undefined, tahap: undefined, q: undefined, mine: undefined })}>
                  Hapus filter
                </button>
              }
            />
          ) : (
            <EmptyState
              icon="documentText"
              title="Belum ada dokumen"
              body={canGenerate(user.role) ? "Dokumen yang Anda buat lewat Chat akan tercatat di sini." : "Dokumen yang dibuat pengguna akan tercatat di sini."}
              action={
                canGenerate(user.role) ? (
                  <a className="btn" href="/">
                    Minta lewat Chat
                  </a>
                ) : undefined
              }
            />
          )
        ) : docs.data ? (
          <DocumentRecords docs={docs.data} catalog={catalog.data} />
        ) : null}
      </section>
    </>
  );
}

export function DocumentRecords({ docs, catalog }: { docs: API.DocumentSummary[]; catalog: API.CatalogTahap[] | null }) {
  return (
    <>
      <div className="records-table">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Judul</th>
              <th scope="col">Tahap</th>
              <th scope="col">Status</th>
              <th scope="col">Pembuat</th>
              <th scope="col" className="num-col">
                Diperbarui
              </th>
              <th scope="col" className="chevron-col" aria-label="Buka" />
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className="row-clickable" onClick={(e) => openFromRow(e, `/dokumen/${d.id}`)}>
                <td>
                  <a className="cell-title cell-link" href={`/dokumen/${d.id}`}>
                    {d.title}
                  </a>
                  <span className="cell-meta">
                    {docTypeLabel(catalog, d.docType)}, versi <span className="num">{d.currentVersion}</span>
                  </span>
                </td>
                <td>{TAHAP_LABEL[d.tahap]}</td>
                <td>
                  <StatusBadge status={d.status} />
                </td>
                <td>{d.createdByName}</td>
                <td className="num-col num">{formatDateTime(d.updatedAt)}</td>
                <td className="chevron-col" aria-hidden="true">
                  <Glyph name="chevronRight" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="rows records-list">
        {docs.map((d) => (
          <li key={d.id}>
            <a className="row row-link" href={`/dokumen/${d.id}`}>
              <span className="row-main">
                <span className="row-title">{d.title}</span>
                <span className="row-meta">
                  {docTypeLabel(catalog, d.docType)}, {d.createdByName}
                </span>
              </span>
              <span className="row-side">
                <StatusBadge status={d.status} />
                <span className="row-meta num">{formatDateTime(d.updatedAt)}</span>
              </span>
              <span className="row-chevron" aria-hidden="true">
                <Glyph name="chevronRight" />
              </span>
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}

/** A click anywhere on a table row opens the record; the title link stays the keyboard and screen reader path. */
function openFromRow(e: { target: EventTarget | null; metaKey: boolean; ctrlKey: boolean }, href: string) {
  const el = e.target as Element | null;
  if (el?.closest("a, button, input, select, textarea")) return;
  if (e.metaKey || e.ctrlKey) return;
  navigate(href);
}
